// src/pages/hr/HRLearning.jsx
// ============================================================
// Learning & Development System (LMS)
// Redesigned with Green Theme and Outline Icons
// ============================================================

import React, { useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { HR_NAV, ADMIN_NAV, LEAD_NAV, EMPLOYEE_NAV } from "../../utils/navigation";
import Layout from "../../components/common/Layout";
import { LearningProvider, useLearningContext } from "../../context/LearningContext";

import LearningOverviewTab from "./learning/LearningOverviewTab";
import CoursesTab from "./learning/CoursesTab";
import MyLearningTab from "./learning/MyLearningTab";
import QuizTab from "./learning/QuizTab";
import LearningReportsTab from "./learning/LearningReportsTab";
import LearningDetailsPanel from "./learning/LearningDetailsPanel";

import {
  Search,
  ArrowUpDown,
  Building,
  LayoutDashboard,
  BookOpen,
  PlayCircle,
  FileCheck,
  FileText,
  GraduationCap,
} from "lucide-react";

function LearningContent() {
  const { userProfile } = useAuth();
  const learningCtx = useLearningContext();
  const { departments } = learningCtx;

  const [activeTab, setActiveTab] = useState("overview");

  // Global Search & Filters
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedDept, setSelectedDept] = useState("all");
  const [sortBy, setSortBy] = useState("name");

  // Details Panel
  const [selectedItemForDetails, setSelectedItemForDetails] = useState(null);

  // 5 Primary Tabs
  const tabs = [
    { id: "overview", label: "Overview", Icon: LayoutDashboard },
    { id: "courses", label: "Courses", Icon: BookOpen },
    { id: "my-learning", label: "My Learning", Icon: PlayCircle },
    { id: "quiz", label: "Quiz", Icon: FileCheck },
    { id: "reports", label: "Reports", Icon: FileText },
  ];

  // Role-based Layout Navigation
  let navItems = HR_NAV;
  let panelLabel = "HR Panel";
  if (userProfile?.role === "admin") {
    navItems = ADMIN_NAV;
    panelLabel = "Admin Panel";
  } else if (userProfile?.role === "lead") {
    navItems = LEAD_NAV;
    panelLabel = "Team Lead Panel";
  } else if (userProfile?.role === "employee") {
    navItems = EMPLOYEE_NAV;
    panelLabel = "Employee Self Service";
  }

  return (
    <Layout navItems={navItems} panelLabel={panelLabel}>
      <style>{`
        .lnd-header-banner {
          background: #1b4b31;
          border-radius: 16px;
          padding: 1.35rem 1.75rem;
          color: #ffffff;
          box-shadow: 0 4px 20px rgba(27, 75, 49, 0.15);
          margin-bottom: 1.25rem;
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-wrap: wrap;
          gap: 1rem;
        }
        .lnd-tabs-bar {
          display: flex;
          gap: 0.5rem;
          border-bottom: 2px solid #e2e8f0;
          margin-bottom: 1.25rem;
          overflow-x: auto;
          padding-bottom: 2px;
          scrollbar-width: thin;
        }
        .lnd-tab-btn {
          padding: 0.65rem 1.25rem;
          background: none;
          border: none;
          font-weight: 600;
          font-size: 0.88rem;
          cursor: pointer;
          color: #64748b;
          border-bottom: 3px solid transparent;
          margin-bottom: -2px;
          white-space: nowrap;
          transition: all 0.2s ease;
          border-radius: 8px 8px 0 0;
          display: inline-flex;
          align-items: center;
          gap: 0.45rem;
        }
        .lnd-tab-btn:hover {
          color: #15803d;
          background: #f0fdf4;
        }
        .lnd-tab-btn.active {
          color: #15803d;
          border-bottom-color: #15803d;
          font-weight: 700;
          background: #f0fdf4;
        }
        .lnd-filter-bar {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          flex-wrap: wrap;
          margin-bottom: 1.25rem;
          padding: 0.85rem 1.1rem;
          background: #ffffff;
          border-radius: 14px;
          border: 1px solid #e2e8f0;
        }
      `}</style>

      {/* Module Header */}
      <div style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        flexWrap: "wrap",
        gap: "1rem",
        marginBottom: "1.25rem"
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: "0.6rem" }}>
          <GraduationCap size={24} strokeWidth={1.8} color="#1b4b31" />
          <h1 style={{ fontSize: "1.5rem", fontWeight: 800, margin: 0, letterSpacing: "-0.02em", color: "#1b4b31" }}>
            Learning & Development System (LMS)
          </h1>
        </div>
      </div>

      {/* ── Global Search & Filters Bar ── */}
      <div className="lnd-filter-bar">
        {/* Global Search */}
        <div style={{ position: "relative", flex: 1, minWidth: "240px" }}>
          <Search size={16} strokeWidth={1.8} style={{ position: "absolute", left: "12px", top: "50%", transform: "translateY(-50%)", color: "#94a3b8" }} />
          <input
            type="text"
            placeholder="Search courses, categories, employees..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: "100%",
              padding: "0.5rem 0.85rem 0.5rem 2.3rem",
              fontSize: "0.84rem",
              borderRadius: "10px",
              border: "1.5px solid #cbd5e1",
              background: "#ffffff",
              outline: "none",
            }}
          />
        </div>

        {/* Filter: Department */}
        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <Building size={15} strokeWidth={1.8} color="#64748b" />
          <select
            value={selectedDept}
            onChange={(e) => setSelectedDept(e.target.value)}
            style={{ padding: "0.5rem 0.85rem", fontSize: "0.82rem", borderRadius: "10px", border: "1.5px solid #cbd5e1", background: "#ffffff", color: "#0f172a", fontWeight: 500 }}
          >
            <option value="all">All Departments</option>
            {departments.map((d) => (
              <option key={d} value={d}>{d}</option>
            ))}
          </select>
        </div>

        {/* Sorting */}
        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <ArrowUpDown size={15} strokeWidth={1.8} color="#64748b" />
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            style={{ padding: "0.5rem 0.85rem", fontSize: "0.82rem", borderRadius: "10px", border: "1.5px solid #cbd5e1", background: "#ffffff", color: "#0f172a", fontWeight: 500 }}
          >
            <option value="name">Sort by Course Name</option>
            <option value="duration">Sort by Duration</option>
            <option value="category">Sort by Category</option>
            <option value="status">Sort by Status</option>
          </select>
        </div>
      </div>

      {/* ── Tab Navigation ── */}
      <div className="lnd-tabs-bar">
        {tabs.map(({ id, label, Icon }) => (
          <button
            key={id}
            className={`lnd-tab-btn ${activeTab === id ? "active" : ""}`}
            onClick={() => {
              setActiveTab(id);
              setSelectedItemForDetails(null);
            }}
          >
            <Icon size={16} strokeWidth={1.8} />
            {label}
          </button>
        ))}
      </div>

      {/* ── Tab Panels ── */}
      <div>
        {activeTab === "overview" && (
          <LearningOverviewTab
            learningState={learningCtx}
            departments={departments}
            currentUser={userProfile}
            onNavigateTab={setActiveTab}
            onSelectCourse={setSelectedItemForDetails}
          />
        )}

        {activeTab === "courses" && (
          <CoursesTab
            learningState={learningCtx}
            departments={departments}
            currentUser={userProfile}
            onSelectCourse={setSelectedItemForDetails}
          />
        )}

        {activeTab === "my-learning" && (
          <MyLearningTab
            learningState={learningCtx}
            currentUser={userProfile}
            onSelectCourse={setSelectedItemForDetails}
          />
        )}

        {activeTab === "quiz" && (
          <QuizTab
            learningState={learningCtx}
            currentUser={userProfile}
            onSelectCourse={setSelectedItemForDetails}
          />
        )}

        {activeTab === "reports" && (
          <LearningReportsTab
            learningState={learningCtx}
            currentUser={userProfile}
          />
        )}
      </div>

      {/* ── Slide-out Details Drawer ── */}
      {selectedItemForDetails && (
        <LearningDetailsPanel
          item={selectedItemForDetails}
          onClose={() => setSelectedItemForDetails(null)}
          learningState={learningCtx}
          currentUser={userProfile}
        />
      )}
    </Layout>
  );
}

export default function HRLearning() {
  return (
    <LearningProvider>
      <LearningContent />
    </LearningProvider>
  );
}
